import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule, KeyValue } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BackendApiService } from '../../../../../app/services/backend-api.service';
import { FormsModule } from '@angular/forms';
import { BaseUrlConstantsService } from '../../../../../app/services/base-url-constants.service';

// Interfaces matching your React structure
interface AlertSetting {
  type: 'event' | 'time';
  event_counter: number;
  id: number;
}

interface AlertSettings {
  [roleId: string]: {
    [categoryId: string]: {
      [alertTypeId: string]: AlertSetting;
    };
  };
}

@Component({
  selector: 'app-alert-settings',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    FormsModule
  ],
  templateUrl: './alert-settings.component.html',
  styleUrl: './alert-settings.component.scss'
})
export class AlertSettingsComponent implements OnInit {
  alertSettings: AlertSettings = {};
  roleMap: { [key: string]: string } = {};
  categoryMap: { [key: string]: string } = {};
  alertTypeMap: { [key: string]: string } = {};

  updating = false;
  hasChanges = false;
  expandedRoles = new Set<string>();

  // Dropdown Options
  eventOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "999", label: "Never" }
  ];

  timeOptions = [
    { value: "1", label: "1hr" },
    { value: "3", label: "3hr" },
    { value: "6", label: "6hr" },
    { value: "24", label: "24hr" },
    { value: "999", label: "Never" }
  ];

  constructor(
    public dialogRef: MatDialogRef<AlertSettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: BackendApiService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    if (this.data && this.data.alertSettingsData) {
      const body = this.data.alertSettingsData;
      this.alertSettings = body.alert_settings || {};
      this.roleMap = body.role_map || {};
      this.categoryMap = body.category_map || {};
      this.alertTypeMap = body.alert_type_map || {};

      // Logic to expand all roles on mobile, or first on desktop
      const roleIds = Object.keys(this.alertSettings);
      const isMobile = window.innerWidth < 768;

      if (isMobile) {
        roleIds.forEach(id => this.expandedRoles.add(id));
      } else if (roleIds.length > 0) {
        this.expandedRoles.add(roleIds[0]);
      }
    }
  }

  toggleRoleExpansion(roleId: string) {
    if (this.expandedRoles.has(roleId)) {
      this.expandedRoles.delete(roleId);
    } else {
      this.expandedRoles.add(roleId);
    }
  }

  isExpanded(roleId: string): boolean {
    return this.expandedRoles.has(roleId);
  }

  // Preserve original order of object keys (KeyValuePipe defaults to sorting by key)
  originalOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => {
    return 0;
  }

  onSettingChange(roleId: string, categoryId: string, alertTypeId: string, newValue: string) {
    if (this.alertSettings[roleId][categoryId][alertTypeId]) {
      this.alertSettings[roleId][categoryId][alertTypeId].event_counter = Number(newValue);
      this.hasChanges = true;
    }
  }

  async handleUpdate() {
    this.updating = true;
    const updatePromises: Promise<any>[] = [];

    // Loop through all settings to find IDs and current values
    Object.values(this.alertSettings).forEach(role =>
      Object.values(role).forEach(category =>
        Object.values(category).forEach(alert => {
          if (alert.id) {
            const url = `${BaseUrlConstantsService.BASE_ALERT_URL}alert/settings/${alert.id}`;
            const payload = { event_counter: alert.event_counter };
            updatePromises.push(this.service.putData(url, payload));
          }
        })
      )
    );

    try {
      await Promise.all(updatePromises);
      this.hasChanges = false;
      this.showToast('Alert Settings updated successfully', 'success');
      this.dialogRef.close(true); // Close on success
    } catch (error) {
      console.error("Error saving alert settings:", error);
      this.showToast('Failed to save alert settings. Please try again.', 'error');
    } finally {
      this.updating = false;
    }
  }

  showToast(message: string, type: 'success' | 'error') {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: type === 'success' ? ['success-snackbar'] : ['error-snackbar']
    });
  }
}